import BaseButton from 'components/ui/BaseButton';
import DropdownMenu from 'components/ui/DropdownMenu';
import { ATTRIB } from 'constants/test';
import { IconProps, MenuItem } from 'interfaces';
import { cloneElement, ReactElement, useCallback, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { getNavigateOptions } from 'utils/navBar/sideNav.util';

interface CollapsedNavItemProps {
  text?: string;
  path?: string;
  icon?: React.ReactNode;
  subMenus?: MenuItem[];
  attribAction?: string;
  renderSubMenuStyle: (subPath?: string) => string;
}

const CollapsedNavItem = ({
  path,
  icon,
  subMenus,
  attribAction,
  renderSubMenuStyle
}: CollapsedNavItemProps) => {
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const handleSubMenuClick = useCallback(
    (menuItem: MenuItem) => {
      if (menuItem.path) {
        navigate(menuItem.path, getNavigateOptions(menuItem.path));
      }
    },
    [navigate]
  );

  const hasSubMenus = subMenus && subMenus.length > 0;

  const iconElement = useMemo(() => {
    if (!icon) return null;
    const isCurrentPage = path && pathname.endsWith(path);
    return (
      <span
        className={`flex-shrink-0 transition-colors duration-200 ${
          isCurrentPage ||
          subMenus?.some(
            (subMenu) => subMenu.path && pathname.endsWith(subMenu.path)
          )
            ? 'text-primary-900 dark:text-white scale-125'
            : 'text-gray-900 dark:text-secondary-100 group-hover:text-primary-900 dark:group-hover:text-white'
        }`}>
        {cloneElement(icon as ReactElement<IconProps>, {
          fill: 'currentColor',
          fillDarkMode: 'currentColor',
          className: '~w-4/5 ~h-4/5'
        })}
      </span>
    );
  }, [icon, subMenus, pathname, path]);

  const baseButton = (
    <BaseButton
      icon={iconElement}
      onClick={() => {
        if (!hasSubMenus && path) {
          navigate(path, getNavigateOptions(path));
        }
      }}
      data-test={attribAction}
      data-testid={ATTRIB.DATA_TESTID.NAV_MENU_MAIN_BTN}
      aria-haspopup={!!subMenus?.length}
      aria-expanded={!!subMenus?.length}
      variant='icon'
      additionalStyles='mx-auto'
    />
  );

  if (!hasSubMenus) {
    return baseButton;
  }

  return (
    <DropdownMenu
      trigger='click'
      subMenus={subMenus}
      onItemClick={handleSubMenuClick}
      renderSubMenuStyle={renderSubMenuStyle}
      contentClassName='shadow rounded-md'>
      {baseButton}
    </DropdownMenu>
  );
};

export default CollapsedNavItem;
